import React, { useEffect } from "react";
import { useAuth } from "../AuthContext";
import { useNavigate } from "react-router-dom";

export default function GoogleOneTap() {
  const { googleLogin, user } = useAuth();
  const navigate = useNavigate();
  const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

  useEffect(() => {
    // Don't show if user is already logged in
    if (user) {
      console.log('[GoogleOneTap] User already logged in, skipping One Tap');
      return;
    }
    
    if (!clientId) {
      console.error('[GoogleOneTap] Google Client ID not found in environment variables');
      return;
    }

    console.log('[GoogleOneTap] Initializing Google One Tap...');

    // Load Google Identity Services script
    const scriptId = 'google-one-tap-script';
    if (document.getElementById(scriptId)) {
      console.log('[GoogleOneTap] Script already loaded, initializing...');
      initializeOneTap();
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.id = scriptId;
    
    script.onload = () => {
      console.log('[GoogleOneTap] Google script loaded successfully');
      // Wait 5 seconds after page load, then show One Tap
      setTimeout(() => {
        console.log('[GoogleOneTap] 5 seconds elapsed, showing prompt...');
        initializeOneTap();
      }, 5000);
    };

    script.onerror = () => {
      console.error('[GoogleOneTap] Failed to load Google Identity Services script');
    };

    document.body.appendChild(script);

    return () => {
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, [user, clientId]);

  const initializeOneTap = () => {
    if (!window.google) {
      console.error('[GoogleOneTap] Google object not found on window');
      return;
    }
    
    if (!clientId) {
      console.error('[GoogleOneTap] Client ID is missing');
      return;
    }

    try {
      console.log('[GoogleOneTap] Initializing with client ID:', clientId);
      window.google.accounts.id.initialize({
        client_id: clientId,
        callback: handleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
        context: 'signin',
        ux_mode: 'popup',
        itp_support: true,
      });

      console.log('[GoogleOneTap] Prompting user...');
      // Display the One Tap prompt in top-right corner
      window.google.accounts.id.prompt((notification) => {
        console.log('[GoogleOneTap] Prompt notification:', notification);
        if (notification.isNotDisplayed()) {
          console.warn('[GoogleOneTap] One Tap not displayed. Reason:', notification.getNotDisplayedReason());
        } else if (notification.isSkippedMoment()) {
          console.warn('[GoogleOneTap] One Tap skipped. Reason:', notification.getSkippedReason());
        } else if (notification.isDismissedMoment()) {
          console.warn('[GoogleOneTap] One Tap dismissed. Reason:', notification.getDismissedReason());
        }
      });
    } catch (err) {
      console.error('[GoogleOneTap] Error initializing:', err);
    }
  };

  const handleCredentialResponse = async (response) => {
    console.log('[GoogleOneTap] Credential response received');
    try {
      const idToken = response?.credential;
      if (!idToken) {
        console.error('[GoogleOneTap] No credential received from Google');
        throw new Error('No credential received from Google');
      }
      
      console.log('[GoogleOneTap] Calling backend googleLogin...');
      const result = await googleLogin(idToken);
      
      const step = result?.onboardingStep ?? 7;
      console.log('[GoogleOneTap] Login successful, onboardingStep:', step);
      setTimeout(() => {
        // Redirect to onboarding if not yet complete
        if (step < 7) {
          navigate('/onboarding');
        } else {
          navigate('/');
        }
      }, 1000);
    } catch (err) {
      console.error('[GoogleOneTap] Login failed:', err);
      alert('Google login failed. Please try again or use email/password login.');
    }
  };

  // This component doesn't render anything visible
  return null;
}
